// @ts-nocheck
import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Tabs } from 'sonata-react-components';
import { isDOOHCampaign } from 'sonata-static-storage/lib/helpers/campaign.helpers';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { ReportingMetricsAndKpisSelector } from '@/modules/reporting/ReportingMetricsAndKpisSelector';
import { reportingProgressSetMetricAndKpi, reportingResetKpiStats } from '@/store/actions/reporting.actions';
import { getReportingType } from '@/store/selectors/reporting.selectors';
import { getCampaigns, getChartTimeStep, getTimeZone } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import ReportingEventsChart from './ReportingEventsChart/ReportingEventsChart';
import './impressionsKpisChart.scss';

type State = {
  disaggregateByOptionSelected?: string;
  disaggregateOptionSelected: string;
  metricsKpis: React.ComponentProps<typeof ReportingEventsChart>['metricsKpis'];
  showAllLegend: boolean;
};

const ImpressionsKpisChart: React.VFC = () => {
  const { formatMessage } = useIntl();
  const reportingCampaignsIds = useSelector(getCampaigns);
  const dispatch = useDispatch();

  const timeStep = useSelector(getChartTimeStep);
  const timezone = useSelector(getTimeZone);
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const isDooh = isDOOHCampaign(useSelector(getReportingType));

  const [{ disaggregateByOptionSelected, disaggregateOptionSelected, metricsKpis, showAllLegend }, setState] =
    useState<State>({
      disaggregateOptionSelected: 'none',
      metricsKpis: {
        metric: {
          id: '',
          label: ''
        },
        ...(isDooh && {
          kpi: {
            id: '',
            label: ''
          }
        })
      },
      showAllLegend: false
    });

  const disaggregatedSelectorPermissions = checkUserResourcePermissions(
    'reportingKPIsGraphsDisaggregatedSelector',
    user,
    userRoles
  );

  const onClickApplyButton = (metricsKpisSelected: State['metricsKpis'], option: string, by: string) => {
    dispatch(reportingResetKpiStats());
    dispatch(
      reportingProgressSetMetricAndKpi({
        kpi: metricsKpisSelected.kpi,
        metric: metricsKpisSelected.metric
      })
    );
    setState(prevState => ({
      ...prevState,
      disaggregateByOptionSelected: by,
      disaggregateOptionSelected: option,
      metricsKpis: metricsKpisSelected
    }));
  };

  const impressionsKpisChartTabOptions = [
    {
      component: (
        <div className="impressionsKpisChart__content">
          <ReportingEventsChart
            cids={reportingCampaignsIds}
            country={user.country}
            disaggregated={disaggregateOptionSelected}
            disaggregatedBy={disaggregateByOptionSelected}
            graphLabel={`${metricsKpis.metric.label} ${isDooh ? '' : `& ${metricsKpis.kpi?.label}`}`}
            groupBy={timeStep}
            hideKpis={isDooh}
            metricsKpis={metricsKpis}
            showAllLegend={showAllLegend}
            timezone={timezone}
          />
        </div>
      ),
      id: 'campaign_calendar',
      title: formatMessage({
        defaultMessage: 'Campaign calendar',
        id: 'reporting.impressionsKpisChart.tabOptions.campaignCalendar'
      })
    },
    {
      component: (
        <div className="impressionsKpisChart__content">
          <ReportingEventsChart
            cids={reportingCampaignsIds}
            country={user.country}
            disaggregated={disaggregateOptionSelected}
            disaggregatedBy={disaggregateByOptionSelected}
            graphLabel={formatMessage({
              defaultMessage: 'Daily',
              id: 'reporting.impressionsKpisChart.graphTitles.title.daily'
            })}
            groupBy="dow"
            hideKpis={isDooh}
            metricsKpis={metricsKpis}
            showAllLegend={showAllLegend}
            timezone={timezone}
          />
          <ReportingEventsChart
            cids={reportingCampaignsIds}
            country={user.country}
            disaggregated={disaggregateOptionSelected}
            disaggregatedBy={disaggregateByOptionSelected}
            graphLabel={formatMessage({
              defaultMessage: 'Hourly',
              id: 'reporting.impressionsKpisChart.graphTitles.title.hourly'
            })}
            groupBy="hod"
            hideKpis={isDooh}
            metricsKpis={metricsKpis}
            showAllLegend={showAllLegend}
            timezone={timezone}
          />
        </div>
      ),
      id: 'daily_and_hourly',
      title: formatMessage({
        defaultMessage: 'Daily & Hourly',
        id: 'reporting.impressionsKpisChart.tabOptions.dailyAndHourly'
      })
    }
  ];

  const toggleChartLegend = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      showAllLegend: !showAllLegend
    }));
  }, [showAllLegend]);

  const labelText = formatMessage({
    defaultMessage: 'KPIs you want to show',
    id: 'reporting.impressionsKpisChart.selectorLabel'
  });

  return (
    <div className="impressionsKpisChart">
      <div className="impressionsKpisChart__selectorsContainer">
        <ReportingMetricsAndKpisSelector
          disaggregatedSelector
          hideKpis={isDooh}
          labelText={labelText}
          onClickApplyButton={onClickApplyButton}
        />
      </div>
      {disaggregatedSelectorPermissions && (
        <Checkbox
          active={showAllLegend}
          classes="impressionsKpisChart__switchLegend"
          disabled={disaggregateOptionSelected === 'none'}
          id="impressionsKpisChart_switchLegend"
          labelColor="#505969"
          labelText={formatMessage({
            defaultMessage: 'Show all legend',
            id: 'reporting.impressionsKpisChart.showAllLegend'
          })}
          onClickAction={toggleChartLegend}
          type="switch"
        />
      )}
      <Tabs initialTab={impressionsKpisChartTabOptions[0].id} tabs={impressionsKpisChartTabOptions} />
    </div>
  );
};

export default React.memo(ImpressionsKpisChart);
